import React, { useState, useEffect, memo } from "react";
import configServ from "../../../services/config";
import { useSelector } from "react-redux";
import { Checkbox, FormControlLabel, FormLabel, Grid, TextField, Button, Box } from "@mui/material";
import SaveIcon from '@mui/icons-material/Save';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import AlertDialog from "../../AlertDialog/AlertDialog";

function ConfigurationCard({ isEdit, setIsEdit, isChanged, setIsChanged, editData, setEditData }) {
  //#region code
  const { company_id, user_id, companyName } = useSelector((state) => state.GlobalVariables);
  const [validationErrors, setValidationErrors] = useState({});
  const initialFormData = {
    mail_to: "",
    mail_cc: "",
    message: "",
    invoice_notification: false,
    sale_notification: false,
    einvoice_notification: false,
    ewaybill_notification: false,
    status: true,
  };
  const [formData, setFormData] = useState(initialFormData);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogContent, setDialogContent] = useState('');

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  useEffect(() => {
    if (Object.keys(editData).length !== 0) {
      setFormData(editData);
    }
  }, [editData])

  const handleSave = async () => {
    try {
      const error = handleFormValidation();
      if (error === false) {
        return false;
      }
      setValidationErrors({});

      // Prepare the data object with "TO" and "CC" fields along with checkbox values
      const requestData = {
        CompanyId: company_id,
        CompanyName: companyName,
        AdminId: user_id,
        mail_to: formData.mail_to,
        mail_cc: formData.mail_cc,
        message: formData.message,
        einvoice_notification: formData.einvoice_notification,
        ewaybill_notification: formData.ewaybill_notification,
        invoice_notification: formData.invoice_notification,
        sale_notification: formData.sale_notification,
        notification: (formData.einvoice_notification === true || formData.ewaybill_notification === true || formData.invoice_notification === true || formData.sale_notification === true) ? true : false,
        entity_type: "",
        status: formData.status,
      };

      if (!isEdit) {
        const response = await configServ.addMailConfiguration(requestData);
        if (response.status === 200) {
          setIsChanged(!isChanged);
          handleReset();
          handleOpenDialog('Data saved successfully.');
        }
        else {
          handleOpenDialog(response.error);
        }
      }
      else {
        const response = await configServ.editMailConfigurations(requestData);
        if (response.status === 200) {
          setIsChanged(!isChanged);
          handleReset();
          handleOpenDialog('Data updated successfully.');
        }
        else {
          handleOpenDialog(response.error);
        }
      }
    }
    catch (error) {
      console.error("Error saving data:", error);
    }
  };

  const handleFormValidation = () => {
    const errors = {};
    if (!formData.mail_to) {
      errors.mail_to = "required";
    }
    if (!formData.mail_cc) {
      errors.mail_cc = "required";
    }
    setValidationErrors(errors);
    return Object.values(errors).every((error) => error === "");
  };

  const handleReset = () => {
    setFormData(initialFormData);
    setValidationErrors({});
    setIsEdit(false);
    setEditData({});
  }

  const handleOpenDialog = (content) => {
    setDialogContent(content);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setDialogContent('');
  };
  //#endregion

  return (
    <>
      <AlertDialog
        open={dialogOpen}
        setOpen={handleCloseDialog}
        title="Alert"
        message={dialogContent}
        buttonTitle="Ok"
      />
      <Box style={{ width: '100%', backgroundColor: '#e3f3fc', padding: 20, border: 'none' }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <FormLabel error={!!validationErrors.mail_to}>TO*</FormLabel>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Enter email address"
              sx={{ backgroundColor: 'white' }}
              name="mail_to"
              value={formData.mail_to}
              onChange={handleInputChange}
              error={!!validationErrors.mail_to}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormLabel error={!!validationErrors.mail_cc}>CC*</FormLabel>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Enter email address"
              sx={{ backgroundColor: 'white' }}
              name="mail_cc"
              value={formData.mail_cc}
              onChange={handleInputChange}
              error={!!validationErrors.mail_cc}
            />
          </Grid>
          <Grid item xs={12} md={12}>
            <FormLabel>Message</FormLabel>
            <TextField
              fullWidth
              variant="outlined"
              multiline
              rows={4}
              placeholder="Enter message"
              sx={{ backgroundColor: 'white' }}
              name="message"
              value={formData.message}
              onChange={handleInputChange}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!formData.invoice_notification}
                  onChange={handleInputChange}
                  name="invoice_notification"
                />
              }
              label="Invoice Notification"
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!formData.sale_notification}
                  onChange={handleInputChange}
                  name="sale_notification"
                />
              }
              label="Sale Notification"
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!formData.einvoice_notification}
                  onChange={handleInputChange}
                  name="einvoice_notification"
                />
              }
              label="E-Invoice Notification"
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!formData.ewaybill_notification}
                  onChange={handleInputChange}
                  name="ewaybill_notification"
                />
              }
              label="E-Way Bill"
            />
          </Grid>

          <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'flex-start' }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!formData.status}
                  onChange={handleInputChange}
                  name="status"
                />
              }
              label="Status"
            />
          </Grid>

          <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="contained" color="primary" onClick={handleSave} startIcon={<SaveIcon />} sx={{ margin: '10px' }}>
              {isEdit ? 'Update' : 'Save'}
            </Button>
            <Button variant="contained" color="secondary" onClick={handleReset} startIcon={<RestartAltIcon />} sx={{ margin: '10px' }}>
              Reset
            </Button>
          </Grid>

        </Grid>
      </Box>
    </>
  );
}

export default ConfigurationCard;
